import { useId, type ReactNode } from 'react';
import { useIssueReportOffer } from '@/hooks/useIssueReportOffer';
import { fillOfferLine, fillOfferTokens, usesOfferTokens } from '@/lib/utils/offer-tokens.utils';
import type { ComparisonCardContent, ComparisonContent } from './service-content';

export interface CompareBlockProps {
  content: ComparisonContent;
  leftCta: ReactNode;
  rightCta: ReactNode;
}

/** "Choose the right support for your raise", from the CMS block, as two side-by-side options. */
export function CompareBlock({ content, leftCta, rightCta }: CompareBlockProps) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={content.title ? headingId : undefined}
      className="bg-surface-card border-border-subtle rounded-card flex flex-col gap-5 border p-6"
    >
      <div className="flex flex-col gap-2">
        {content.eyebrow && (
          <p className="text-dkgreen text-xs font-semibold tracking-wide uppercase">
            {content.eyebrow}
          </p>
        )}
        {content.title && (
          <h3 id={headingId} className="text-h4 text-navy">
            {content.title}
          </h3>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {content.leftCard && <Option card={content.leftCard} cta={leftCta} />}
        {content.rightCard && <Option card={content.rightCard} cta={rightCta} />}
      </div>
    </section>
  );
}

function Option({ card, cta }: { card: ComparisonCardContent; cta: ReactNode }) {
  const { data: offer } = useIssueReportOffer({
    enabled: usesOfferTokens(card.description, card.priceLine),
  });
  const description = card.description ? fillOfferTokens(card.description, offer) : '';
  const priceLine = fillOfferLine(card.priceLine, offer);
  return (
    <div className="border-border-subtle flex flex-col gap-3 rounded-lg border p-5">
      {card.title && <p className="text-navy font-semibold">{card.title}</p>}
      {description && <p className="text-text-secondary text-sm leading-6">{description}</p>}
      {priceLine && <p className="text-navy text-sm font-semibold">{priceLine}</p>}
      {/* `flex` gives the CTA link a real box, so its padding takes up space
          instead of spilling over the price line and the card's edge. */}
      <div className="mt-auto flex pt-1">{cta}</div>
    </div>
  );
}
